import React, { useState, useCallback, useMemo } from "react";
import styles from "./style.module.scss";
import useResidualStore from "../../../../stores/residuals";
import useDomainStore from "../../../../stores/domain";
import MerchantNameLink from "../../../../components/MerchantNameLink";
import CaretDown from "../../../../assets/svg/icons/common/CaretDown";
import Download from "../../../../assets/svg/icons/common/Download";
import CaretRight from "../../../../assets/svg/icons/common/CaretRight";
import Pagination from "../../../../components/Pagination/Pagination";
import SkeletonRow from "../../../../components/skeleton-loader/table/row";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import toast from "react-hot-toast";
import { fetchResidualMerchantExport } from "../../../../services/residuals";

// Currency formatter helper
const formatCurrency = (amount) => {
  if (!amount) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(parseFloat(amount)));
};

const formatSignedCurrency = (amount) => {
  const numericAmount = Number(amount) || 0;
  return numericAmount < 0
    ? `-${formatCurrency(numericAmount)}`
    : formatCurrency(numericAmount);
};

const ResidualTable = () => {
  const { isKurvDomain } = useDomainStore();
  const [view, setView] = useState("processors"); // "processors" | "products"
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = useCallback((id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const isRowExpanded = useCallback(
    (id) => {
      return expandedRows[id] || false;
    },
    [expandedRows],
  );

  const getIndentLevel = (type) => {
    switch (type) {
      case "office":
        return 1;
      case "branch":
        return 2;
      case "rep":
        return 3;
      case "merchant":
        return 4;
      default:
        return 0;
    }
  };

  const {
    residuals,
    loading: rawLoading,
    pagination,
    filters,
    dataMode,
    setFilters,
    fetchResidualsExportData,
  } = useResidualStore((state) => ({
    residuals: state.residuals.raw,
    loading: state.loading.residuals,
    pagination: state.residuals.pagination,
    filters: state.filters,
    dataMode: state.residuals.dataMode,
    setFilters: state.setFilters,
    fetchResidualsExportData: state.fetchResidualsExportData,
  }));

  const displayMode = filters.displayMode || "hierarchy";
  // Treat the gap between "user toggled mode" and "new data arrived" as
  // loading. Otherwise the previous mode's data renders into the new mode's
  // table for a frame and the hierarchy expand/collapse arrows disappear
  // (flat rows have no `branches` array).
  const loading = rawLoading || displayMode !== (dataMode || "hierarchy");

  const handleDisplayModeChange = (mode) => {
    if (mode === displayMode) return;
    setFilters({
      ...filters,
      displayMode: mode,
      pagination: {
        page: 1,
        per_page: mode === "flat" ? 50 : 10,
      },
    });
  };

  const [isExporting, setIsExporting] = useState(false);
  const [downloadingMerchantKey, setDownloadingMerchantKey] = useState(null);

  const onExportClickHandler = async () => {
    try {
      setIsExporting(true);
      await fetchResidualsExportData(filters, true);
      toast.success("CSV downloaded successfully");
    } catch (error) {
      toast.error("Failed to download CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const handleMerchantRowDownload = async (merchant, e) => {
    e.stopPropagation();
    const key = merchant.id || `${merchant.mid}-${merchant.office}-${merchant.branch}-${merchant.rep_code}`;
    setDownloadingMerchantKey(key);
    try {
      await fetchResidualMerchantExport(filters, merchant);
      toast.success("CSV downloaded");
    } catch {
      /* toast handled in service */
    } finally {
      setDownloadingMerchantKey(null);
    }
  };

  const [sortConfig, setSortConfig] = useState({
    field: filters.sort?.field || "dba",
    direction: filters.sort?.direction || "asc",
  });

  const renderHeader = () => (
    <div className={styles.header}>
      <div className={styles.tabs}>
        {view === "processors" ? (
          <button
            className={`${styles.tab} ${view === "processors" ? styles.active : ""}`}
            onClick={() => setView("processors")}
          >
            All processors
          </button>
        ) : (
          <>
            <button
              className={`${styles.tab} ${selectedProduct === "all" ? styles.active : ""}`}
              onClick={() => setSelectedProduct("all")}
            >
              All products
            </button>
            <button
              className={`${styles.tab} ${selectedProduct === "amex" ? styles.active : ""}`}
              onClick={() => setSelectedProduct("amex")}
            >
              AmEx
            </button>
            <button
              className={`${styles.tab} ${selectedProduct === "altus" ? styles.active : ""}`}
              onClick={() => setSelectedProduct("altus")}
            >
              Altus
            </button>
            <button
              className={`${styles.tab} ${selectedProduct === "altusPremier" ? styles.active : ""}`}
              onClick={() => setSelectedProduct("altusPremier")}
            >
              Altus Premier
            </button>
            <button
              className={`${styles.tab} ${selectedProduct === "geti" ? styles.active : ""}`}
              onClick={() => setSelectedProduct("geti")}
            >
              GETI
            </button>
          </>
        )}
      </div>

      <div className={styles.actions}>
        {view === "processors" && (
          <div className={styles.display_mode_toggle}>
            <button
              type="button"
              className={`${styles.display_mode_btn} ${displayMode === "hierarchy" ? styles.display_mode_btn_active : ""}`}
              onClick={() => handleDisplayModeChange("hierarchy")}
            >
              Hierarchy
            </button>
            <button
              type="button"
              className={`${styles.display_mode_btn} ${displayMode === "flat" ? styles.display_mode_btn_active : ""}`}
              onClick={() => handleDisplayModeChange("flat")}
            >
              Flat
            </button>
          </div>
        )}
        {view === "processors" && (
          <ButtonIcon
            bgColor={"#EDEDF4"}
            onClick={onExportClickHandler}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Download />
                Downloading...
              </>
            ) : (
              <>
                <Download />
                Download Expanded View
              </>
            )}
          </ButtonIcon>
        )}
      </div>
    </div>
  );

  const renderRow = (row, type = "office", parentExpanded = true) => {
    const rowId =
      type === "office"
        ? `office-${row.office}`
        : type === "branch"
          ? `branch-${row.office}-${row.branch}`
          : type === "rep"
            ? `rep-${row.office}-${row.branch}-${row.rep_code}`
            : `merchant-${row.mid}-${row.date || ""}`; // Add date to make merchant keys unique

    const isExpanded = isRowExpanded(rowId);
    const indentLevel = getIndentLevel(type);
    const showRow = parentExpanded;

    if (!showRow) return null;

    return (
      <React.Fragment key={rowId}>
        <tr
          className={type === "merchant" ? styles.data_row : styles.group_row}
        >
          <td
            style={{
              paddingLeft: `${indentLevel * 24}px`,
              lineHeight: "2",
              fontSize: type === "merchant" && "14px",
              fontWeight: type === "merchant" && "500",
              fontFamily: type === "merchant" && "Work Sans",
            }}
          >
            {((type === "office" && row.branches) ||
              (type === "branch" && row.reps) ||
              (type === "rep" && row.data)) && (
              <button
                className={`${styles.toggle_btn} ${isExpanded ? styles.expanded : ""}`}
                onClick={() => toggleRow(rowId)}
              >
                <CaretRight />
              </button>
            )}
            {type === "merchant" ? (
              <MerchantNameLink powerappId={row.powerapp_id}>
                {row.dba}
              </MerchantNameLink>
            ) : (
              <>
                {type === "office" && "OFFICE: "}
                {type === "branch" && "BRANCH: "}
                {type === "rep" && "REP CODE: "}
                {type === "office"
                  ? row.office
                  : type === "branch"
                    ? row.branch
                    : row.rep_code}
              </>
            )}
          </td>
          <td>
            {/* adding ?? if mid is not defined  */}
            {type === "merchant" ? `***${String(row.mid ?? "").slice(-6)}` : ""}
          </td>
          <td>{type === "merchant" ? row.processor : ""}</td>
          <td>{formatCurrency(row.bc_volume)}</td>
          <td>{row.bc_auth}</td>
          <td>{row.batch_header_trans}</td>
          <td>{formatCurrency(row.ic_income)}</td>
          <td className={row.ic_expense < 0 ? styles.negative : ""}>
            {formatCurrency(row.ic_expense)}
          </td>
          <td className={row.line_total < 0 ? styles.negative : ""}>
            {formatCurrency(row.line_total)}
          </td>
          <td className={row.residual_earning < 0 ? styles.negative : ""}>
            {row.line_margin}%
          </td>
          <td
            className={`${styles.highlight} ${row.residual_earning < 0 ? styles.negative : ""}`}
            style={row.residual_earning < 0 ? { color: "red" } : {}}
          >
            {row.residual_earning < 0
              ? `-${formatCurrency(row.residual_earning)}`
              : formatCurrency(row.residual_earning)}
          </td>
          <td className={row.residual_margin < 0 ? styles.negative : ""}>
            {row.residual_margin}%
          </td>
          <td>
            {type === "merchant" ? (
              <ButtonIcon
                bgColor="#EDEDF4"
                className={styles.merchantDownloadBtn}
                title="Download this merchant residual row (CSV)"
                disabled={
                  downloadingMerchantKey ===
                  (row.id || `${row.mid}-${row.office}-${row.branch}-${row.rep_code}`)
                }
                onClick={(e) => handleMerchantRowDownload(row, e)}
              >
                Download
              </ButtonIcon>
            ) : null}
          </td>
        </tr>
        {isExpanded &&
          type === "office" &&
          row.branches?.map((branch) =>
            renderRow(branch, "branch", isExpanded),
          )}
        {isExpanded &&
          type === "branch" &&
          row.reps?.map((rep) => renderRow(rep, "rep", isExpanded))}
        {isExpanded &&
          type === "rep" &&
          row.data?.map((merchant) =>
            renderRow(merchant, "merchant", isExpanded),
          )}
      </React.Fragment>
    );
  };

  // Handle sorting
  const handleSort = (field) => {
    const direction =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction });

    // Update filters and fetch data
    const newFilters = {
      ...filters,
      sort: { field, direction },
      pagination: {
        ...filters.pagination,
        page: 1,
      },
    };
    setFilters(newFilters);
  };

  // Render sort indicator
  const renderSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return null;
    }
    return (
      <CaretDown
        className={`${styles.sort_icon} ${sortConfig.direction === "desc" ? styles.sort_desc : ""}`}
      />
    );
  };

  const totals = useMemo(() => {
    if (!residuals || !Array.isArray(residuals)) return 0;
    return residuals.reduce(
      (acc, item) => ({
        bc_volume: acc.bc_volume + (parseFloat(item.bc_volume) || 0),
        bc_auth: acc.bc_auth + (parseInt(item.bc_auth, 10) || 0),
        batch_header_trans:
          acc.batch_header_trans + (parseFloat(item.batch_header_trans) || 0),
        ic_income: acc.ic_income + (parseFloat(item.ic_income) || 0),
        ic_expense: acc.ic_expense + (parseFloat(item.ic_expense) || 0),
        line_total: acc.line_total + (parseFloat(item.line_total) || 0),
        residual_earning:
          acc.residual_earning + (parseFloat(item.residual_earning) || 0),
      }),
      {
        bc_volume: 0,
        bc_auth: 0,
        batch_header_trans: 0,
        ic_income: 0,
        ic_expense: 0,
        line_total: 0,
        residual_earning: 0,
      },
    );
  }, [residuals]);

  const renderFlatTable = () => (
    <div className={styles.table_wrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Office</th>
            <th>Branch</th>
            <th>Rep</th>
            <th onClick={() => handleSort("dba")} className={styles.sortable}>
              DBA {renderSortIcon("dba")}
            </th>
            <th onClick={() => handleSort("mid")} className={styles.sortable}>
              MID {renderSortIcon("mid")}
            </th>
            <th
              onClick={() => handleSort("processor")}
              className={styles.sortable}
            >
              Processor {renderSortIcon("processor")}
            </th>
            <th
              onClick={() => handleSort("bc_volume")}
              className={styles.sortable}
            >
              BC Volume {renderSortIcon("bc_volume")}
            </th>
            <th
              onClick={() => handleSort("bc_auth")}
              className={styles.sortable}
            >
              BC Auth {renderSortIcon("bc_auth")}
            </th>
            <th
              onClick={() => handleSort("batch_header_trans")}
              className={styles.sortable}
            >
              Batch Hdr {renderSortIcon("batch_header_trans")}
            </th>
            <th
              onClick={() => handleSort("ic_income")}
              className={styles.sortable}
            >
              IC Inc {renderSortIcon("ic_income")}
            </th>
            <th
              onClick={() => handleSort("ic_expense")}
              className={styles.sortable}
            >
              IC Exp {renderSortIcon("ic_expense")}
            </th>
            <th
              onClick={() => handleSort("line_total")}
              className={styles.sortable}
            >
              Line total {renderSortIcon("line_total")}
            </th>
            <th
              onClick={() => handleSort("line_margin")}
              className={styles.sortable}
            >
              Line margin {renderSortIcon("line_margin")}
            </th>
            <th
              onClick={() => handleSort("residual_earning")}
              className={styles.sortable}
            >
              Res. earned {renderSortIcon("residual_earning")}
            </th>
            <th
              onClick={() => handleSort("residual_margin")}
              className={styles.sortable}
            >
              Res. margin {renderSortIcon("residual_margin")}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 8 }).map((_, index) => (
                <SkeletonRow key={`skeleton-${index}`} columnLength={15} />
              ))
            : residuals?.length > 0 &&
              residuals.map((row) => (
                <tr key={row.id} className={styles.data_row}>
                  <td>{row.office}</td>
                  <td>{row.branch}</td>
                  <td>{row.rep_code}</td>
                  <td>{row.dba}</td>
                  <td>{`***${String(row.mid ?? "").slice(-6)}`}</td>
                  <td>{row.processor}</td>
                  <td>{formatCurrency(row.bc_volume)}</td>
                  <td>{row.bc_auth}</td>
                  <td>{row.batch_header_trans}</td>
                  <td>{formatCurrency(row.ic_income)}</td>
                  <td className={row.ic_expense < 0 ? styles.negative : ""}>
                    {formatCurrency(row.ic_expense)}
                  </td>
                  <td className={row.line_total < 0 ? styles.negative : ""}>
                    {formatCurrency(row.line_total)}
                  </td>
                  <td className={row.line_margin < 0 ? styles.negative : ""}>
                    {row.line_margin}%
                  </td>
                  <td
                    className={`${styles.highlight} ${row.residual_earning < 0 ? styles.negative : ""}`}
                    style={
                      row.residual_earning < 0 ? { color: "red" } : {}
                    }
                  >
                    {row.residual_earning < 0
                      ? `-${formatCurrency(row.residual_earning)}`
                      : formatCurrency(row.residual_earning)}
                  </td>
                  <td
                    className={row.residual_margin < 0 ? styles.negative : ""}
                  >
                    {row.residual_margin}%
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
      {!loading && (!residuals || residuals.length === 0) && (
        <div className={styles.empty_state}>No data to display</div>
      )}
    </div>
  );

  const renderHierarchyTable = () => (
    <div className={styles.table_wrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th onClick={() => handleSort("dba")} className={styles.sortable}>
              DBA {renderSortIcon("dba")}
            </th>
            <th onClick={() => handleSort("mid")} className={styles.sortable}>
              MID {renderSortIcon("mid")}
            </th>
            <th
              onClick={() => handleSort("processor")}
              className={styles.sortable}
            >
              Processor {renderSortIcon("processor")}
            </th>
            <th
              onClick={() => handleSort("bc_volume")}
              className={styles.sortable}
            >
              BC Volume {renderSortIcon("bc_volume")}
            </th>
            <th
              onClick={() => handleSort("bc_auth")}
              className={styles.sortable}
            >
              BC Auth {renderSortIcon("bc_auth")}
            </th>
            <th
              onClick={() => handleSort("batch_header_trans")}
              className={styles.sortable}
            >
              Batch Hdr {renderSortIcon("batch_header_trans")}
            </th>
            <th
              onClick={() => handleSort("ic_income")}
              className={styles.sortable}
            >
              IC Inc {renderSortIcon("ic_income")}
            </th>
            <th
              onClick={() => handleSort("ic_expense")}
              className={styles.sortable}
            >
              IC Exp {renderSortIcon("ic_expense")}
            </th>
            <th
              onClick={() => handleSort("line_total")}
              className={styles.sortable}
            >
              Line total {renderSortIcon("line_total")}
            </th>
            <th
              onClick={() => handleSort("line_margin")}
              className={styles.sortable}
            >
              Line margin {renderSortIcon("line_margin")}
            </th>
            <th
              onClick={() => handleSort("residual_earning")}
              className={styles.sortable}
            >
              Res. earned {renderSortIcon("residual_earning")}
            </th>
            <th
              onClick={() => handleSort("residual_margin")}
              className={styles.sortable}
            >
              Res. margin {renderSortIcon("residual_margin")}
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 8 }).map((_, index) => (
                <SkeletonRow key={`skeleton-${index}`} columnLength={13} />
              ))
            : residuals?.length > 0
              ? residuals.map((office) => renderRow(office))
              : null}
        </tbody>
        {!loading && residuals && residuals.length > 0 && (
          <tfoot>
            <tr>
              <td>Total</td>
              <td></td>
              <td></td>
              <td>{formatCurrency(totals.bc_volume)}</td>
              <td>{totals.bc_auth}</td>
              <td>{totals.batch_header_trans}</td>
              <td>{formatCurrency(totals.ic_income)}</td>
              <td style={{ color: totals.ic_expense < 0 ? "red" : "inherit" }}>
                {formatSignedCurrency(totals.ic_expense)}
              </td>
              <td style={{ color: totals.line_total < 0 ? "red" : "inherit" }}>
                {formatSignedCurrency(totals.line_total)}
              </td>
              <td></td>
              <td
                style={{
                  color: totals.residual_earning < 0 ? "red" : "inherit",
                }}
              >
                {formatSignedCurrency(totals.residual_earning)}
              </td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>
      {!loading && (!residuals || residuals.length === 0) && (
        <div className={styles.empty_state}>No data to display</div>
      )}
    </div>
  );

  const renderProcessorsTable = () =>
    displayMode === "flat" ? renderFlatTable() : renderHierarchyTable();

  const renderProductRow = (row, parentExpanded = true) => {
    const isExpanded = isRowExpanded(row.id);
    const indentLevel = getIndentLevel(row.type);
    const showRow = parentExpanded;

    if (!showRow) return null;

    return (
      <React.Fragment key={row.id}>
        <tr
          className={
            row.type === "merchant" ? styles.data_row : styles.group_row
          }
        >
          <td
            style={{
              paddingLeft: `${indentLevel * 24}px`,
              lineHeight: "2",
            }}
          >
            {row.children && (
              <button
                className={`${styles.toggle_btn} ${isExpanded ? styles.expanded : ""}`}
                onClick={() => toggleRow(row.id)}
              >
                <CaretRight />
              </button>
            )}
            {row.type === "merchant" ? (
              <MerchantNameLink powerappId={row.powerapp_id}>
                {row.dba}
              </MerchantNameLink>
            ) : (
              <>
                {row.type === "office" && "OFFICE: "}
                {row.type === "branch" && "BRANCH: "}
                {row.type === "rep" && "REP CODE: "}
                {row.type === "product" && ""}
                {row.type === "product" ? row.name : row.code}
              </>
            )}
          </td>
          <td>{row.type === "merchant" ? row.mid : ""}</td>
          <td>{row.type === "merchant" ? row.dateAdded : ""}</td>
          <td>
            {row.type === "merchant" && (
              <div className={styles.status}>
                {row.status === "active" ? (
                  <span className={styles.active}>Active</span>
                ) : (
                  <span className={styles.na}>N/A</span>
                )}
              </div>
            )}
          </td>
          <td>{row.transactions || "0"}</td>
          <td className={row.total_payout < 0 ? styles.negative : ""}>
            {row.total_payout < 0
              ? `(${formatCurrency(row.total_payout)})`
              : formatCurrency(row.total_payout)}
          </td>
          <td>{row.split_rate ? `${row.split_rate.toFixed(2)}%` : ""}</td>
          <td className={row.agent_residual < 0 ? styles.negative : ""}>
            {row.agent_residual < 0
              ? `(${formatCurrency(row.agent_residual)})`
              : formatCurrency(row.agent_residual)}
          </td>
        </tr>
        {row.children &&
          isExpanded &&
          row.children.map((child) => renderProductRow(child, isExpanded))}
      </React.Fragment>
    );
  };

  const renderAllProductsTable = () => {
    const products = residuals?.products || {};
    const productSummaries = Object.values(products).map((product) => {
      const totalTransactions = product.children.reduce((sum, office) => {
        return sum + (office.transactions || 0);
      }, 0);
      const totalPayout = product.children.reduce((sum, office) => {
        return sum + (office.total_payout || 0);
      }, 0);
      const agentResidual = product.children.reduce((sum, office) => {
        return sum + (office.agent_residual || 0);
      }, 0);

      return {
        product: product.name,
        transactions: totalTransactions,
        total_payout: totalPayout,
        split_rate: 60.0, // This could be dynamic if needed
        agent_residual: agentResidual,
      };
    });

    return (
      <div className={styles.table_wrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                Product <CaretDown />
              </th>
              <th>Total Transactions</th>
              <th>Total Payout</th>
              <th>Split Rate %</th>
              <th>Agent residual</th>
            </tr>
          </thead>
          <tbody>
            {productSummaries.map((row, index) => (
              <tr
                key={index}
                className={row.total_payout < 0 ? styles.negative : ""}
              >
                <td>{row.product}</td>
                <td>{row.transactions}</td>
                <td className={row.total_payout < 0 ? styles.negative : ""}>
                  {row.total_payout < 0
                    ? `(${formatCurrency(row.total_payout)})`
                    : formatCurrency(row.total_payout)}
                </td>
                <td>{row.split_rate.toFixed(2)}%</td>
                <td className={row.agent_residual < 0 ? styles.negative : ""}>
                  {row.agent_residual < 0
                    ? `(${formatCurrency(row.agent_residual)})`
                    : formatCurrency(row.agent_residual)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDetailedProductTable = () => {
    const products = residuals?.products || {};
    const selectedProductData = products[selectedProduct]?.children || [];

    return (
      <div className={styles.table_wrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                DBA <CaretDown />
              </th>
              <th>MID</th>
              <th>DATE ADDED</th>
              <th>CHECK SERVICE ST...</th>
              <th>TOTAL TRANSACTIONS</th>
              <th>TOTAL PAYOUT</th>
              <th>SPLIT RATE %</th>
              <th>AGENT RESIDUAL</th>
            </tr>
          </thead>
          <tbody>
            {selectedProductData.map((office, index) => (
              <React.Fragment
                key={office.id || `product-${selectedProduct}-${index}`}
              >
                {renderProductRow(office)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderProductsTable = () => {
    if (view === "products" && selectedProduct === "all") {
      return renderAllProductsTable();
    }
    return renderDetailedProductTable();
  };

  const renderPagination = () => {
    if (pagination.total === 0) return null;

    return (
      <div className={styles.pagination_container}>
        <Pagination
          pageCount={pagination.last_page}
          currentPage={pagination.current_page - 1}
          onPageChange={({ selected }) => {
            const newPage = selected + 1;
            // Update filters with new page number
            const newFilters = {
              ...filters,
              pagination: {
                ...filters.pagination,
                page: newPage,
              },
            };
            setFilters(newFilters);
          }}
          totalItems={pagination.total}
          itemsPerPage={pagination.per_page}
          showTotalItems={pagination.total > 0}
        />
      </div>
    );
  };

  return (
    <div
      className={`${styles.container} ${isKurvDomain ? styles.kurv_theme : ""}`}
    >
      {renderHeader()}
      {view === "processors" ? renderProcessorsTable() : renderProductsTable()}
      {renderPagination()}
    </div>
  );
};

export default ResidualTable;
